import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, LogOut, ExternalLink, BellRing } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, TOKEN_KEY } from "@/lib/api";
import { CATEGORIES, categoryOf } from "@/lib/store";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { ThemeToggle } from "@/components/site/ThemeToggle";
import { ProductForm } from "@/components/admin/ProductForm";
import { AdminProductRow } from "@/components/admin/AdminProductRow";
import { TopSearches } from "@/components/admin/TopSearches";
import { OrderPricesTable } from "@/components/admin/OrderPricesTable";
import { CardRatesTable } from "@/components/admin/CardRatesTable";
import { AnalyticsPanel } from "@/components/admin/AnalyticsPanel";
import { TradeRequests } from "@/components/admin/TradeRequests";
import { TradePricesTable } from "@/components/admin/TradePricesTable";
import { DeliverySettings } from "@/components/admin/DeliverySettings";
import { ProposalBuilder } from "@/components/admin/proposal/ProposalBuilder";
import { ShipmentsPanel } from "@/components/admin/shipments/ShipmentsPanel";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-2xl border border-white/10 bg-[#111113] p-5">
    <p className="mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">{label}</p>
    <p data-testid={testid} className="font-display mt-2 text-3xl font-bold text-white">{value}</p>
  </div>
);

export default function Admin() {
  const [user, setUser] = useState(null);
  const [products, setProducts] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [cat, setCat] = useState("todos");

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/products");
    setProducts(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) load().catch((e) => toast.error(formatApiError(e)));
  }, [user, load]);

  const testPush = async () => {
    try {
      const { data } = await api.post("/admin/push/test");
      toast.success(data?.sent ? `Notificação de teste enviada para ${data.sent} aparelho(s)` : "Ninguém ativou os alertas ainda");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const logout = async () => {
    await api.post("/auth/logout").catch(() => null);
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  };

  const save = async (payload) => {
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Aparelho atualizado" : draft ? "Cópia adicionada à vitrine" : "Aparelho adicionado à vitrine");
      setFormOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const patch = async (p, changes, message) => {
    const { id, created_at, sold_at, ...rest } = p;
    try {
      await api.put(`/admin/products/${id}`, { ...rest, ...changes });
      toast.success(message);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggle = (p) =>
    p.status === "vendido"
      ? patch(p, { status: "disponivel" }, "De volta à vitrine")
      : patch(p, { status: "vendido" }, "Marcado como vendido");

  const toggleFeatured = (p) =>
    p.featured
      ? patch(p, { featured: false, featured_until: null }, "Destaque removido")
      : patch(p, { featured: true, featured_until: new Date(Date.now() + 7 * 86400000).toISOString() }, "Oferta da semana por 7 dias (altere a data em Editar)");

  const remove = async (p) => {
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Aparelho excluído");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openForm = (p = null) => {
    setEditing(p);
    setDraft(null);
    setFormOpen(true);
  };

  // Abre um novo cadastro já preenchido com os dados do aparelho; só é criado ao salvar.
  const duplicate = (p) => {
    const { id, created_at, sold_at, ...rest } = p;
    setEditing(null);
    setDraft({ ...rest, status: "disponivel", featured: false, featured_until: null });
    setFormOpen(true);
  };

  if (user === null) return <div className="min-h-screen bg-[#09090b]" />;

  return (
    <div data-testid="admin-page-container" className="min-h-screen bg-[#09090b] text-white">
      {!user ? (
        <AdminLogin onLogin={setUser} />
      ) : (
        <div className="mx-auto max-w-5xl px-5 py-8 sm:py-12">
          <header className="flex items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2.5" data-testid="admin-home-link">
              <img src="/brand/mark-white.png" alt="RC" className="h-7 w-auto" />
              <span className="font-light">iPhones</span>
            </Link>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <button data-testid="admin-push-test" onClick={testPush} title="Enviar notificação de teste" className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:bg-white/10"><BellRing className="h-4 w-4" /> <span className="hidden sm:inline">Testar alerta</span></button>
              <Link to="/" target="_blank" data-testid="admin-view-site" className="hidden sm:flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:bg-white/10"><ExternalLink className="h-4 w-4" /> Ver site</Link>
              <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:bg-white/10"><LogOut className="h-4 w-4" /> Sair</button>
            </div>
          </header>

          <div className="mt-10 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="mono text-[11px] uppercase tracking-[0.28em] text-zinc-400">Painel do lojista</p>
              <h1 className="font-display mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight">Meus produtos</h1>
            </div>
            <button data-testid="admin-add-product-button" onClick={() => openForm()} className="flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 font-semibold text-black transition-transform hover:scale-[1.03] active:scale-95">
              <Plus className="h-4 w-4" /> Adicionar produto
            </button>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-3">
            <Stat label="Total" value={products.length} testid="admin-stat-total" />
            <Stat label="Na vitrine" value={products.filter((p) => p.status === "disponivel").length} testid="admin-stat-available" />
            <Stat label="Vendidos" value={products.filter((p) => p.status === "vendido").length} testid="admin-stat-sold" />
          </div>

          <ProposalBuilder products={products.filter((p) => p.status === "disponivel")} />
          <ShipmentsPanel />
          <TradeRequests />
          <AnalyticsPanel />
          <TopSearches />
          <OrderPricesTable />
          <CardRatesTable />
          <TradePricesTable />
          <DeliverySettings />

          <div className="no-scrollbar mt-8 flex gap-2 overflow-x-auto" data-testid="admin-category-filter">
            {[{ id: "todos", label: "Todos" }, ...CATEGORIES].map((c) => (
              <button key={c.id} data-testid={`admin-category-${c.id}`} onClick={() => setCat(c.id)}
                className={`shrink-0 rounded-full border px-4 py-2 text-sm transition-colors ${cat === c.id ? "border-white bg-white text-black" : "border-white/15 text-zinc-200 hover:bg-white/10"}`}>
                {c.label}
              </button>
            ))}
          </div>

          <div className="mt-4 space-y-3" data-testid="admin-product-list">
            {products.length === 0 && <p className="rounded-2xl border border-dashed border-white/15 p-10 text-center text-zinc-400">Nenhum produto cadastrado ainda. Clique em “Adicionar produto”.</p>}
            {products.filter((p) => cat === "todos" || categoryOf(p) === cat).map((p) => <AdminProductRow key={p.id} p={p} onEdit={openForm} onDuplicate={duplicate} onToggle={toggle} onFeature={toggleFeatured} onDelete={remove} />)}
          </div>
        </div>
      )}
      <ProductForm open={formOpen} onOpenChange={setFormOpen} initial={editing || draft} mode={editing ? "edit" : draft ? "copy" : "new"} onSave={save} />
    </div>
  );
}
